import { store } from 'store/index'
import { messagesActions, selectChatId } from 'store/messages'
import { captchaAxios } from 'api/config'
import ObjectID from 'bson-objectid'
import axios from 'axios'
import { UploadService } from '@runelab/odin-common'
import { ChatRoleEnum, Participant } from 'types/chat'
import { buildAgentInputs } from 'utils/agent-inputs'

export const uploadFiles = async (files: File[]) => {
  if (files.length === 0) return []
  const service = new UploadService(axios)
  return await Promise.all(
    files.map((file) => {
      return service.uploadFile({
        file,
        uploadFileParam: {
          additionalInfo: {},
          linkedEntityId: '',
          linkedEntityType: '',
        },
      })
    }),
  )
}

type AskBotAnswerParams = {
  question: string
  questionId: string
  attachmentIds?: string[]
}

export const askBotAnswer = async ({ question, questionId, attachmentIds }: AskBotAnswerParams) => {
  const state = store.getState()
  const chatId = selectChatId(state)
  const answerId = String(ObjectID())

  // Forward every injected input the agents declared (required or optional).
  const inputs = buildAgentInputs(state)

  // This project always runs agentic: the supervisor answers from documentation (RAG as a tool)
  // and delegates live data to the always-on Qlik agent. The plain RAG endpoint is never used here.
  console.debug({ answerId, questionId, attachmentIds, agentic: true })

  if (chatId) {
    store.dispatch(messagesActions.setChatStatus({ chatId, status: 'awaitingBot' }))
  }

  try {
    const { data } = await captchaAxios.post<{ message: string }>(
      `chats/${chatId}/answer/bot`,
      { attachmentIds, inputs },
      {
        params: {
          question,
          questionId,
          answerId,
          agentic: true,
        },
      },
    )

    // The backend now acks immediately and streams the answer over WS (to avoid the 60s HTTP
    // timeout), so we must NOT reset the status here: keep 'awaitingBot' (dots stay up) until the
    // first stream arrives (-> 'streaming') and end_stream flips it to 'idle' in useChatWebSocket.
    // Fallback: if the full answer still comes back in the body (non-streaming), render it and
    // reset to 'idle' ourselves, since no stream/end_stream will follow.
    if (data?.message && chatId) {
      const chat = store.getState().messageState.chats[chatId]
      const bot = chat?.participants?.find((participant: Participant) => participant.role === ChatRoleEnum.BOT)
      store.dispatch(
        messagesActions.upsertBotMessage({
          chatId,
          botMessageId: answerId,
          partialResponse: data.message,
          senderId: bot?.userId ?? 'bot',
        }),
      )
      store.dispatch(messagesActions.setChatStatus({ chatId, status: 'idle' }))
    }
  } catch (error) {
    // Ack failed: no stream will arrive, so clear the indicator here.
    if (chatId) {
      store.dispatch(messagesActions.setChatStatus({ chatId, status: 'idle' }))
    }
    return Promise.reject(error)
  }
}
